import { UISref } from "@uirouter/react";
import { FunctionComponent, useState } from "react";
import styled, { css } from "styled-components";
import { PrimaryButton } from "../../../components/button";
import { Flex } from "../../../components/layout";
import { Loader } from "../../../components/loader";
import { AuthDTO } from "../../../store/auth/models/dto";
import { Auth } from "../../../store/auth/reducer";
import { useFetchable } from "../../../store/common/fetchable";
import { ReactComponent as AppStoreIcon } from "../../../assets/app-store.svg";
import { ReactComponent as GooglePlayIcon } from "../../../assets/google-play.svg";

const Container = styled(Flex)`
  flex-direction: column;
`;

const blurring = css`
  filter: blur(4px);
`;

const SecretKey = styled.div<{ blurred: boolean }>`
  ${(props) => (props.blurred ? blurring : "")}
`;

const SecretKeyDescription = styled(SecretKey)`
  margin: 2em 0;
  font-size: 0.8em;
`;

const QR = styled.img<{ blurred: boolean }>`
  width: 15em;
  height: auto;
  transition: filter 0.1s ease-in-out;
  ${(props) => (props.blurred ? blurring : "")}
`;

const CTAWrapper = styled.div`
  margin-top: 3em;
  margin-bottom: 2em;
`;

const Title = styled.h1`
  text-align: center;
`;

namespace Badge {
  export const Container = styled(Flex)`
    justify-content: space-around;
    height: 4em;
    align-items: stretch;
    width: 100%;
  `;

  export const Wrapper = styled.a`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 50%;
  `;

  export const Image = styled.img<{ padded?: boolean }>`
    border-radius: 0.2em;
    height: auto;
    margin: auto;
    ${(props) => (props.padded ? "padding: 0.5em" : "")};
  `;
}

const PresentationalShowQRCodePage: FunctionComponent<{
  login: AuthDTO.Responses.Login.WithTwoFA;
}> = ({ login }) => {
  const [blurred, setBlurred] = useState(true);

  return (
    <Container>
      <Title className="h1">Add to Google Authenticator</Title>
      {login.twoFactorAuth.extraDetails ? (
        <>
          <QR
            blurred={blurred}
            src={`data:image/png;base64, ${login.twoFactorAuth.extraDetails.image}`}
          />
          <SecretKeyDescription blurred={blurred}>
            or enter the following secret key
          </SecretKeyDescription>
          <SecretKey blurred={blurred}>
            {login.twoFactorAuth.extraDetails.secret}
          </SecretKey>
        </>
      ) : (
        "To set up or reset 2FA please contact customer support"
      )}
      <CTAWrapper>
        {blurred && login.twoFactorAuth.extraDetails ? (
          <PrimaryButton fullWidth onClick={() => setBlurred(false)}>
            Reveal
          </PrimaryButton>
        ) : (
          <UISref to="^.2fa">
            <a>
              <PrimaryButton fullWidth>Take me back</PrimaryButton>
            </a>
          </UISref>
        )}
      </CTAWrapper>
      <Badge.Container>
        <Badge.Wrapper href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&hl=en&gl=US&pcampaignid=pcampaignidMKT-Other-global-all-co-prtnr-py-PartBadge-Mar2515-1">
          <GooglePlayIcon />
        </Badge.Wrapper>
        <Badge.Wrapper href="https://apps.apple.com/us/app/google-authenticator/id388497605?itsct=apps_box_badge&amp;itscg=30200">
          <AppStoreIcon />
        </Badge.Wrapper>
      </Badge.Container>
    </Container>
  );
};

export const ShowQRCodePage = () => {
  const login = useFetchable(Auth.login, { username: "", password: "" }); // TODO makes no sense to default this fetchable

  return (
    <Loader data={[login]}>
      {
        ([data]) =>
          AuthDTO.Responses.Login.isWithTwoFA(data) ? (
            <PresentationalShowQRCodePage login={data} />
          ) : (
            <></>
          ) // TODO handle properly
      }
    </Loader>
  );
};
